// src/pages/MePage.tsx — Slice D commit 3.
//
// The /me tab content. Replaces the Slice B "Coming soon" stub with a
// real account page composed of:
//   1. Optional "Complete your profile" nudge banner (when the user
//      skipped onboarding earlier).
//   2. Profile ticket — brass-gradient Card with tear-off notches, the
//      user's avatar, a NYMR-ID eyebrow, name, departments line, and a
//      two-column bottom row with Station + a sage "Active" Stamp.
//   3. Line-manager card — SSO users only (Phase 9 follow-up 2026-05-21:
//      PIN volunteers no longer have a line manager surface here).
//   4. Three sub-page links (My details, Change my PIN, Privacy).
//   5. Sign out button + footer.
//
// Audit-row divergences from prototype/apps2.jsx lines 376-493 baked in:
//   - NYMR ID line: drops the hard-coded `1042`; renders just `· SSO|PIN`
//     when member_id_number is null.
//   - Role line under name: uses departments.join(" · "), not user.role.
//   - Member-since column: replaced with a sage "Active" Stamp (no join
//     date in the schema).
//   - Manager card only shown for SSO sessions — PIN volunteers don't
//     report into the NYMR line-management chain.
//   - Routes via react-router (not the prototype's openSub callback).
//   - PIN link conditional on authMethod.

import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { apiFetch, ApiError } from "../api";
import { useCurrentUser, useLogout, type MeResponse } from "../auth";
import { LogoutOnDutyModal } from "../components/duty/LogoutOnDutyModal";
import { GroupSelfJoin } from "../components/groupqm/GroupSelfJoin";
import { C, F } from "../theme";
import {
  Avatar,
  Button,
  Card,
  Eyebrow,
  Icons,
  Stamp,
  TopBar,
} from "../components/ui";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ContactRow {
  id: number;
  first: string;
  last: string;
  role: string | null;
  isManager: boolean;
}

interface ContactsListResponse {
  contacts: ContactRow[];
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function MePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { me, refresh } = useCurrentUser();
  const signOut = useLogout();
  const [signingOut, setSigningOut] = useState(false);
  // Sign-In System (Build 1) -- intercept Sign out when on duty (PHASE_8_ADDENDUM §4).
  const [logoutOnDutyOpen, setLogoutOnDutyOpen] = useState(false);
  const [flash, setFlash] = useState<string | null>(
    typeof location.state === "object" && location.state !== null && "flash" in location.state
      ? String((location.state as { flash?: unknown }).flash ?? "")
      : null,
  );

  useEffect(() => {
    if (!flash) return;
    const t = window.setTimeout(() => {
      setFlash(null);
      window.history.replaceState({}, "", location.pathname);
    }, 3000);
    return () => window.clearTimeout(t);
  }, [flash, location.pathname]);

  if (!me) {
    return (
      <div>
        <TopBar title="My account" />
        <div style={{ padding: "18px 16px" }}>
          <div
            style={{
              height: 240,
              borderRadius: 18,
              background: C.paperWarm,
              border: `1px solid ${C.hairlineLt}`,
            }}
            aria-hidden="true"
          />
        </div>
      </div>
    );
  }

  const showNudge = me.onboarded_at !== null && me.onboarded_skipped === true;
  // Phase 9 follow-up (2026-05-21) — PIN volunteers don't surface a line
  // manager. The Card is SSO-only.
  const showLineManager = me.authMethod !== "pin";

  return (
    <div>
      <TopBar title="My account" />
      <div
        style={{
          padding: "14px 16px 24px",
          display: "flex",
          flexDirection: "column",
          gap: 14,
        }}
      >
        {flash && (
          <div
            role="status"
            aria-live="polite"
            style={{
              background: C.sageBg,
              border: `1px solid ${C.sageLt}`,
              borderRadius: 12,
              padding: "10px 14px",
              fontFamily: F.body,
              fontSize: 14,
              fontWeight: 700,
              color: C.sageDk,
            }}
          >
            {flash}
          </div>
        )}

        {showNudge && <NudgeBanner onTap={() => navigate("/onboarding")} />}

        <ProfileTicket me={me} />

        {me.member_id_number === null && (
          <MemberIdNudge onTap={() => navigate("/me/member-id")} />
        )}

        {showLineManager && <LineManagerCard me={me} onSaved={refresh} />}

        <SubPageList me={me} navigate={navigate} />

        {/* Sign-In System (Build 2) -- self-join chat groups. Renders nothing
            unless there are self-joinable groups to join or leave. */}
        <GroupSelfJoin me={me} />

        <Button
          variant="danger"
          fullWidth
          disabled={signingOut}
          onClick={async () => {
            if (signingOut) return;
            // On duty? Offer to sign off first (the session otherwise persists
            // across logout — PHASE_8_ADDENDUM §4).
            if (me.dutySession?.active) {
              setLogoutOnDutyOpen(true);
              return;
            }
            setSigningOut(true);
            await signOut();
          }}
        >
          <Icons.Logout s={18} c={C.signalDk} />
          {signingOut ? "Signing out..." : "Sign out"}
        </Button>

        <LogoutOnDutyModal
          open={logoutOnDutyOpen}
          onCancel={() => setLogoutOnDutyOpen(false)}
          onSignOffAndLogout={async () => {
            setSigningOut(true);
            try {
              await apiFetch("/api/duty-sessions/me", { method: "DELETE" });
            } catch {
              /* sign off best-effort; still log out */
            }
            await signOut();
          }}
          onStayOnAndLogout={async () => {
            setSigningOut(true);
            await signOut();
          }}
        />

        <div
          style={{
            textAlign: "center",
            padding: "8px 0",
            fontFamily: F.serif,
            fontStyle: "italic",
            fontSize: 12,
            color: C.inkMute,
          }}
        >
          NYMR Hub · v3.5 preview
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Nudge banner — only when onboarding was skipped
// ---------------------------------------------------------------------------

function NudgeBanner({ onTap }: { onTap: () => void }) {
  return (
    <div
      onClick={onTap}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onTap();
        }
      }}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "12px 14px",
        background: `linear-gradient(135deg, ${C.brassSurf}, ${C.brassBg})`,
        border: `1px solid ${C.brassLt}`,
        borderRadius: 14,
        cursor: "pointer",
        boxShadow: C.shadowS,
      }}
    >
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: 10,
          background: C.brassDk,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Icons.User s={18} c={C.brassGlow} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontFamily: F.serif,
            fontSize: 15,
            fontWeight: 700,
            color: C.brassDk,
            letterSpacing: -0.2,
          }}
        >
          Complete your profile
        </div>
        <div
          style={{
            fontSize: 12.5,
            color: C.inkSoft,
            fontWeight: 600,
            marginTop: 2,
            lineHeight: 1.35,
          }}
        >
          Add your phone and emergency contact — takes 2 minutes.
        </div>
      </div>
      <span
        style={{
          color: C.brassDk,
          fontSize: 20,
          fontWeight: 700,
          flexShrink: 0,
        }}
      >
        ›
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// MemberIdNudge — heather-tinted card, shown when member_id_number is null.
// Slice F adds the self-claim flow at /me/member-id.
// ---------------------------------------------------------------------------

function MemberIdNudge({ onTap }: { onTap: () => void }) {
  return (
    <button
      type="button"
      onClick={onTap}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "12px 14px",
        background: C.heatherBg,
        border: `1px solid ${C.heatherLt}`,
        borderRadius: 14,
        cursor: "pointer",
        boxShadow: C.shadowS,
        width: "100%",
        textAlign: "left",
      }}
    >
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: 10,
          background: C.heatherDk,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Icons.Ticket s={18} c={C.brassGlow} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontFamily: F.serif,
            fontSize: 15,
            fontWeight: 700,
            color: C.heatherDk,
            letterSpacing: -0.2,
          }}
        >
          Link your ID Card Number
        </div>
        <div
          style={{
            fontSize: 12.5,
            color: C.inkSoft,
            fontWeight: 600,
            marginTop: 2,
            lineHeight: 1.35,
          }}
        >
          See your comp tickets and visit history on the Tickets app.
        </div>
      </div>
      <span style={{ color: C.heatherDk, fontSize: 20, fontWeight: 700, flexShrink: 0 }}>›</span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Profile ticket — perforated-ticket aesthetic with tear-off notches
// ---------------------------------------------------------------------------

function ProfileTicket({ me }: { me: MeResponse }) {
  const authBadge = me.authMethod === "sso" ? "SSO" : "PIN";
  const eyebrow =
    me.member_id_number !== null
      ? `ID Card · ${me.member_id_number} · ${authBadge}`
      : `· ${authBadge}`;
  const sub = me.departments.length > 0 ? me.departments.join(" · ") : "—";
  const station = me.departments[0] ?? "—";

  return (
    <div
      style={{
        background: `linear-gradient(135deg, ${C.brassSurf}, ${C.paperSoft})`,
        border: `1px solid ${C.brassLt}`,
        borderRadius: 18,
        boxShadow: C.shadowS,
        position: "relative",
        // overflow:visible so the tear-off notches can render beyond the
        // bounding box.
        overflow: "visible",
      }}
    >
      <div
        style={{
          position: "absolute",
          left: -8,
          top: 60,
          width: 16,
          height: 16,
          borderRadius: 16,
          background: C.paper,
          boxShadow: `inset 1px 0 0 ${C.brassLt}`,
        }}
        aria-hidden="true"
      />
      <div
        style={{
          position: "absolute",
          right: -8,
          top: 60,
          width: 16,
          height: 16,
          borderRadius: 16,
          background: C.paper,
          boxShadow: `inset -1px 0 0 ${C.brassLt}`,
        }}
        aria-hidden="true"
      />
      <div
        style={{
          padding: "18px 18px 14px",
          display: "flex",
          gap: 14,
          alignItems: "center",
        }}
      >
        <Avatar first={me.first} last={me.last} size={64} tone="brass" />
        <div style={{ flex: 1, minWidth: 0 }}>
          <Eyebrow>{eyebrow}</Eyebrow>
          <div
            style={{
              fontFamily: F.serif,
              fontSize: 24,
              fontWeight: 700,
              color: C.ink,
              letterSpacing: -0.4,
              lineHeight: 1.05,
              marginTop: 2,
            }}
          >
            {me.first} {me.last}
          </div>
          <div
            style={{
              fontSize: 13,
              color: C.inkSoft,
              fontWeight: 600,
              marginTop: 4,
            }}
          >
            {sub}
          </div>
        </div>
      </div>
      <div
        style={{
          padding: "12px 18px 16px",
          borderTop: `1px dashed ${C.brassLt}`,
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 14,
          alignItems: "center",
        }}
      >
        <div>
          <div
            style={{
              fontSize: 11,
              color: C.inkMute,
              fontWeight: 700,
              letterSpacing: 0.6,
              textTransform: "uppercase",
            }}
          >
            Station
          </div>
          <div
            style={{
              fontFamily: F.serif,
              fontSize: 15,
              fontWeight: 700,
              color: C.ink,
              marginTop: 2,
              letterSpacing: -0.1,
            }}
          >
            {station}
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <Stamp tone="sage">Active</Stamp>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Line-manager card — SSO sessions only. PIN volunteers never see this.
// Picker opens by default when no manager is set.
// ---------------------------------------------------------------------------

function LineManagerCard({
  me,
  onSaved,
}: {
  me: MeResponse;
  onSaved: () => Promise<void> | void;
}) {
  const navigate = useNavigate();

  // Picker opens by default ONLY when neither a contact-id nor a free-text
  // manager is set — otherwise we show the existing value with an Edit/Change
  // button (free-text Edit links to /me/details for the Slice F fallback).
  const [pickerOpen, setPickerOpen] = useState<boolean>(
    me.line_manager_contact_id === null && !(me.line_manager_name?.trim()),
  );
  const [contacts, setContacts] = useState<ContactRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(me.line_manager_contact_id);

  useEffect(() => {
    setSelectedId(me.line_manager_contact_id);
  }, [me.line_manager_contact_id]);

  useEffect(() => {
    let cancelled = false;
    apiFetch<ContactsListResponse>("/api/contacts")
      .then((data) => {
        if (!cancelled) setContacts(data.contacts);
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setError(e instanceof ApiError ? e.message : "Could not load contacts");
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // The full set of managers — alphabetical by last_name from the list
  // endpoint already. Filter on the response field name (camelCase isManager,
  // not the DB snake_case is_manager).
  const managers = useMemo(() => {
    if (!contacts) return [];
    return contacts.filter((c) => c.isManager);
  }, [contacts]);

  const currentManager = useMemo(() => {
    if (me.line_manager_contact_id === null || !contacts) return null;
    return contacts.find((c) => c.id === me.line_manager_contact_id) ?? null;
  }, [contacts, me.line_manager_contact_id]);

  async function save() {
    setSaving(true);
    setError(null);
    try {
      await apiFetch("/api/me/line-manager", {
        method: "PATCH",
        body: JSON.stringify({ contactId: selectedId }),
      });
      await onSaved();
      setPickerOpen(false);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Save failed");
    } finally {
      setSaving(false);
    }
  }

  // Slice F — when no picker-linked contact, fall back to the free-text
  // line_manager_name from hub_users. Picker still wins when set.
  const freeTextName = me.line_manager_name?.trim() ?? "";
  const showFreeText = !currentManager && !pickerOpen && freeTextName !== "";

  return (
    <Card>
      <Eyebrow>My line manager</Eyebrow>
      {showFreeText && (
        <>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              marginTop: 8,
            }}
          >
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: 12,
                background: C.heatherBg,
                border: `1px solid ${C.heatherLt}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontFamily: F.serif,
                fontSize: 18,
                fontWeight: 700,
                color: C.heatherDk,
                flexShrink: 0,
              }}
              aria-hidden="true"
            >
              {freeTextName.slice(0, 1).toUpperCase()}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontFamily: F.serif,
                  fontSize: 18,
                  fontWeight: 700,
                  color: C.ink,
                  letterSpacing: -0.2,
                }}
              >
                {freeTextName}
              </div>
              <div
                style={{
                  fontFamily: F.serif,
                  fontStyle: "italic",
                  fontSize: 12,
                  color: C.inkMute,
                  fontWeight: 600,
                  marginTop: 2,
                }}
              >
                Not in the contacts directory
              </div>
            </div>
          </div>
          <Button
            variant="secondary"
            fullWidth
            style={{ marginTop: 12 }}
            onClick={() => navigate("/me/details")}
          >
            Edit in My details
          </Button>
        </>
      )}
      {currentManager && !pickerOpen && (
        <>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              marginTop: 8,
            }}
          >
            <Avatar
              first={currentManager.first}
              last={currentManager.last}
              size={48}
              tone="brass"
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontFamily: F.serif,
                  fontSize: 18,
                  fontWeight: 700,
                  color: C.ink,
                  letterSpacing: -0.2,
                }}
              >
                {currentManager.first} {currentManager.last}
              </div>
              {currentManager.role && (
                <div
                  style={{
                    fontSize: 13,
                    color: C.inkSoft,
                    fontWeight: 600,
                    marginTop: 2,
                  }}
                >
                  {currentManager.role}
                </div>
              )}
            </div>
          </div>
          <Button
            variant="secondary"
            fullWidth
            style={{ marginTop: 12 }}
            onClick={() => {
              setPickerOpen(true);
              setSelectedId(me.line_manager_contact_id);
            }}
          >
            Change manager
          </Button>
        </>
      )}

      {pickerOpen && (
        <div style={{ marginTop: 8 }}>
          {!currentManager && (
            <p
              style={{
                fontFamily: F.body,
                fontSize: 14,
                color: C.inkSoft,
                fontWeight: 600,
                margin: "0 0 12px",
                lineHeight: 1.5,
              }}
            >
              Pick your line manager.
            </p>
          )}
          {error && (
            <div
              style={{
                padding: 12,
                marginBottom: 12,
                borderRadius: 12,
                background: C.signalBg,
                border: `1px solid rgba(184,58,43,0.22)`,
                color: C.signalDk,
                fontFamily: F.body,
                fontWeight: 700,
                fontSize: 13,
              }}
            >
              {error}
            </div>
          )}
          {contacts === null ? (
            <div
              style={{
                fontFamily: F.serif,
                fontStyle: "italic",
                color: C.inkMute,
                padding: "10px 0",
              }}
            >
              Loading managers...
            </div>
          ) : managers.length === 0 ? (
            <div
              style={{
                fontFamily: F.serif,
                fontStyle: "italic",
                color: C.inkMute,
                padding: "10px 0",
              }}
            >
              No managers in the directory yet.
            </div>
          ) : (
            <div
              style={{
                display: "flex",
                gap: 6,
                flexWrap: "wrap",
                marginBottom: 12,
              }}
            >
              {managers.map((m) => {
                const active = selectedId === m.id;
                return (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => setSelectedId(m.id)}
                    style={{
                      padding: "9px 14px",
                      borderRadius: 10,
                      border: active
                        ? `1.5px solid ${C.brassDk}`
                        : `1px solid ${C.hairline}`,
                      background: active ? C.brassBg : C.card,
                      color: active ? C.brassDk : C.ink,
                      fontFamily: F.body,
                      fontWeight: 700,
                      fontSize: 14,
                      cursor: "pointer",
                    }}
                  >
                    {m.first} {m.last}
                  </button>
                );
              })}
            </div>
          )}
          <div style={{ display: "flex", gap: 10 }}>
            <Button
              variant="primary"
              fullWidth
              disabled={saving || selectedId === null}
              onClick={save}
            >
              {saving ? "Saving..." : "Save"}
            </Button>
            {currentManager && (
              <Button
                variant="ghost"
                disabled={saving}
                onClick={() => {
                  setPickerOpen(false);
                  setSelectedId(me.line_manager_contact_id);
                  setError(null);
                }}
              >
                Cancel
              </Button>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Sub-page links — three or four rows (PIN hidden for SSO sessions). Slice L
// added "App preferences" alongside Privacy after the inline preferences
// panel moved to /me/preferences.
// ---------------------------------------------------------------------------

interface SubPageRow {
  id: "details" | "pin" | "privacy" | "preferences" | "notifications";
  label: string;
  route: string;
  icon: typeof Icons.User;
  tone: "brass" | "sage" | "heather";
}

function SubPageList({
  me,
  navigate,
}: {
  me: MeResponse;
  navigate: (path: string) => void;
}) {
  const rows: SubPageRow[] = [
    {
      id: "details",
      label: "My details & emergency contacts",
      route: "/me/details",
      icon: Icons.User,
      tone: "brass",
    },
    ...(me.authMethod === "sso"
      ? []
      : ([
          {
            id: "pin" as const,
            label: "Change my PIN",
            route: "/me/pin",
            icon: Icons.Settings,
            tone: "sage" as const,
          },
        ] satisfies SubPageRow[])),
    {
      id: "privacy",
      label: "Privacy & data settings",
      route: "/me/privacy",
      icon: Icons.Settings,
      tone: "heather",
    },
    {
      id: "preferences",
      label: "App preferences",
      route: "/me/preferences",
      icon: Icons.Settings,
      tone: "brass",
    },
    // CODE-701 — Issue Manager notification preferences. Surfaced for
    // all users (SSO + PIN); SMS toggles only fire if the user also sets
    // a verified phone number, which the same page lets them do.
    {
      id: "notifications",
      label: "Notifications",
      route: "/me/notifications",
      icon: Icons.Bell,
      tone: "heather",
    },
  ];

  return (
    <Card padded={false}>
      <div style={{ padding: "4px 16px" }}>
        {rows.map((row, i) => {
          const tile = TILE[row.tone];
          const Ic = row.icon;
          return (
            <button
              key={row.id}
              type="button"
              onClick={() => navigate(row.route)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 14,
                padding: "14px 0",
                width: "100%",
                background: "transparent",
                border: "none",
                borderTop: i === 0 ? "none" : `1px solid ${C.hairlineLt}`,
                cursor: "pointer",
                textAlign: "left",
              }}
            >
              <div
                style={{
                  width: 38,
                  height: 38,
                  borderRadius: 11,
                  background: tile.bg,
                  border: `1px solid ${C.hairlineLt}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <Ic s={18} c={tile.ic} />
              </div>
              <div
                style={{
                  flex: 1,
                  fontFamily: F.body,
                  fontSize: 15,
                  fontWeight: 700,
                  color: C.ink,
                }}
              >
                {row.label}
              </div>
              <span style={{ color: C.inkMute, fontSize: 18 }}>›</span>
            </button>
          );
        })}
      </div>
    </Card>
  );
}

// Tone-disc colour set (matches the prototype's TILE table from shell.jsx).
const TILE: Record<"brass" | "sage" | "heather", { bg: string; ic: string }> = {
  brass: { bg: C.brassBg, ic: C.brassDk },
  sage: { bg: C.sageBg, ic: C.sageDk },
  heather: { bg: C.heatherBg, ic: C.heatherDk },
};
